import React from "react";
import { useLocalStore, useObserver } from "mobx-react";
import { Button, Grid, Paper, Typography } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { SpinnerOverlay, TabBadge } from "@components";
import { useNavigation } from "@navigation";
import { Form, FormxDebugger } from "@common/formx";
import { SubVersionFormStore, defaultFields, FormData } from "./SubVersionFormStore";
import { initialValues as defaultValues } from "./utils";

import { TabFormLayout } from "@common/forms/TabFormLayout";
import { DynamicFieldsGroup } from "@consultants/ConsultantContract/DynamicFieldsGroup";
import DnDContainer from "@containers/DnDContainer/DnDContainer";

type Props = {
  contractId?: string;
  action?: string;
  subVersionId: string;
  initialValues?: FormData;
  onSubmitCB?: () => void;
};

export const SubVersionForm = ({
  subVersionId,
  contractId,
  action,
  initialValues,
  onSubmitCB
}: Props) => {
  const navigator = useNavigation();
  const localStore = useLocalStore(
    () =>
      new SubVersionFormStore(
        initialValues ?? defaultValues,
        contractId,
        () => handleClose(),
        subVersionId,
        action,
        onSubmitCB
      )
  );

  const handleClose = (): void => {
    localStore.form.reset({ ...defaultValues });
    localStore.fields = defaultFields;
    navigator.close();
  };

  return useObserver(() => {
    const { form, loading, fields, isEdit, alreadyUploadedDocument } = localStore;

    const renderMain = (
      <Paper
        elevation={0}
        style={{ height: "100%", padding: "12px 24px 36px", position: "relative" }}
      >
        <DnDContainer
          disabled={alreadyUploadedDocument}
          onDrop={(accepted) => {
            if (accepted[0]) {
              localStore.fileToUpload = accepted[0];
              localStore.form.setValue("fileName", accepted[0].name);
            }
          }}
          style={{
            margin: "-16px -24px",
            padding: "16px 24px"
          }}
        >
          <SpinnerOverlay show={loading} />
          <Typography variant="h4">{locale.AddSubVersion}</Typography>
          <Typography variant="h5">
            {locale.Contract}: {localStore.customerContractName}
          </Typography>
          <DynamicFieldsGroup
            form={form}
            fields={fields}
            extraProps={{ store: localStore }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography>
                {localStore.fileToUpload?.name || localStore.form.values.fileName}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button
                onClick={localStore.resetUpload}
                disabled={!localStore.fileToUpload}
                variant="outlined"
                style={{ marginRight: 10 }}
              >
                {locale.Reset}
              </Button>

              <Button
                color="primary"
                variant="contained"
                disabled={!!localStore.fileToUpload || localStore.alreadyUploadedDocument}
                onClick={localStore.requestUpload}
                style={{ marginLeft: "auto" }}
              >
                {locale.UploadFile}
              </Button>
            </Grid>
          </Grid>
        </DnDContainer>
      </Paper>
    );

    const tabs = [
      {
        label: (
          <TabBadge
            label={isEdit ? locale.Edit : `${locale.New} ${locale.Contract}`}
            hasErrors={false}
          />
        ),
        value: "main",
        content: renderMain
      }
    ];

    return (
      <Form store={form} allowUpdates>
        <TabFormLayout
          blockId="customer_contract_subVersion_form"
          canSubmit={form.canSubmit}
          isDirty={form.dirty}
          isSubmitting={form.isSubmitting}
          onSubmit={localStore.onSubmit}
          onReset={form.onResetHandler}
          onClose={localStore.handleClose}
          tabs={tabs}
        >
          <FormxDebugger store={localStore.form} />
        </TabFormLayout>

        <FormxDebugger store={form} />
      </Form>
    );
  });
};
